import * as moment from "moment";
import { CostAssessmentRecordRow, DepreciatedRecordRow } from "../../types";
import { RecordOptions } from "./calculation";
import { normalizeRecords } from "./normalize";
import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";

export interface DiminishingBalanceOptions {
  kind: 'diminishing-balance',
  economicLife: number
  depreciationFactorPerYear: number
}

export function diminishingBalanceMethod(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  options: DiminishingBalanceOptions
) {
  const { economicLife, depreciationFactorPerYear } = options
  const { valuationDate, buildingConstructionDate, size } = recordOptions
  const _records = normalizeRecords(records, recordOptions)
  const _valuationDate = moment(valuationDate)
  const _buildingConstructionDate = moment(buildingConstructionDate)
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _buildingConstructionDate
  )
  const _factor = depreciationFactorPerYear / 100
  if (isNaN(yearDiff)) return null;
  const _mappedRecords: DepreciatedRecordRow[] = _records.map(item => {
    return {
      ...item,
      ...depreciatedCost(
        item.total_cost,
        _factor,
        yearDiff,
        size
      )
    }
  })

  const _totalCost = _records.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.total_cost
    },
    0
  )
  const totalDepreciatedCost = _mappedRecords.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.depreciated_cost
    },
    0
  )

  return {
    records: _mappedRecords,
    graphValues: graphValues(_totalCost, _factor, economicLife),
    totalCost: _totalCost,
    totalDepreciatedCost,
    yearDiff
  }
}

function depreciatedCost(
  totalCost: number,
  depreciationFactorPerYear: number,
  yearDiff: number,
  size: number
): {
  depreciated_cost: number,
  depreciated_per_unit: number
} {
  const depreciated_cost = depreciatedValue(totalCost, depreciationFactorPerYear, yearDiff)
  return {
    depreciated_cost,
    depreciated_per_unit: depreciated_cost / size
  }
}

function depreciatedValue(
  totalCost: number,
  factor: number,
  year: number
): number {
  return totalCost * Math.pow((1 - factor), year)
}

function graphValues(
  totalCost: number,
  factor: number,
  economicLife: number
)  {
  const values: {age: number, depreciationFactor: number, depreciatedValue: number}[] = []
  for (let i = 0; i <= economicLife; i++) {
    const _depreciatedValue = depreciatedValue(
      totalCost,
      factor,
      i
    )
    const depreciationFactor = i === 0
      ? 0
      : values[i - 1].depreciatedValue - _depreciatedValue
    values.push({
      age: i,
      depreciatedValue: _depreciatedValue,
      depreciationFactor
    })
  }
  return values
}